// Pure helpers for reading a post's signed media: what kind an item is, which
// item a card should render, and whether its link has already expired. Nothing
// here touches Firebase or the DOM, so it all runs under Jest.

import { MediaKind, SignedMedia } from '../types/post';

/** Mirrors `ALLOWED_VIDEO_CONTENT_TYPES` in the backend's `posts.util.ts`. */
export const VIDEO_CONTENT_TYPES = ['video/mp4', 'video/webm'];

/**
 * A stored `kind` wins when it is one of the two literals; anything else —
 * including a pre-KAN-14 entry that has none — falls back to deriving it from
 * the content type. A line-for-line mirror of the backend's `readMediaKind`,
 * so both sides classify the same item the same way.
 */
export function readMediaKind(entry: { kind?: unknown; contentType?: unknown }): MediaKind {
  if (entry.kind === 'video' || entry.kind === 'image') {
    return entry.kind;
  }
  return typeof entry.contentType === 'string' &&
    VIDEO_CONTENT_TYPES.indexOf(entry.contentType) !== -1
    ? 'video'
    : 'image';
}

/**
 * Whether a card can render this item at all. A video always can; an "image"
 * only when it did not declare some other type, so a PDF is skipped rather
 * than pushed into a broken `<img>`. An item that simply did not say still
 * renders — the URL is the point.
 */
export function isRenderableMedia(entry: SignedMedia): boolean {
  const kind = readMediaKind(entry);
  if (kind === 'video') {
    return true;
  }
  return entry.contentType === '' || entry.contentType.startsWith('image/');
}

/** The first item a timeline card can show, or null when there is none. */
export function pickTimelineMedia(media: SignedMedia[]): SignedMedia | null {
  return media.find(isRenderableMedia) ?? null;
}

/**
 * Whether a signed URL has already lapsed. Fails open: no timestamp and an
 * unparsable one both count as live, because a bad string must never hide a
 * video that would have played. The `<video>` error handler is the backstop.
 */
export function isMediaExpired(expiresAt: string | null, now: Date): boolean {
  if (expiresAt === null) {
    return false;
  }
  const parsed = Date.parse(expiresAt);
  if (Number.isNaN(parsed)) {
    return false;
  }
  return parsed <= now.getTime();
}

/** A clip's real aspect ratio in Plyr's `w:h` form, or null when unknowable. */
export function aspectRatioString(width: number, height: number): string | null {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  return `${Math.round(width)}:${Math.round(height)}`;
}
